/**
 * Analytics view configuration constants
 */

/**
 * Mapping of GeoJSON URLs to display names for analytics
 */
export const ANALYTICS_MODEL_DISPLAY_NAMES = {
  "VALIDATION_PLAN_v2:EU_025_historical_view": "AI Model 025",
  "VALIDATION_PLAN_v2:EU_0125_historical_view": "AI Model 0125",
};

/**
 * Mapping of models to their corresponding metrics names for analytics
 */
export const ANALYTICS_METRICS_NAMES = {
  "VALIDATION_PLAN_v2:EU_025_historical_view": "METRICAS_EU_025",
  "VALIDATION_PLAN_v2:EU_0125_historical_view": "METRICAS_EU_0125",
};

/**
 * Mapping of models to their corresponding crossborder metrics names
 */
export const ANALYTICS_CROSSBORDER_METRICS_NAMES = {
  "VALIDATION_PLAN_v2:EU_025_historical_view": "METRICAS_CROSSBORDER_025",
  "VALIDATION_PLAN_v2:EU_0125_historical_view": "METRICAS_CROSSBORDER_0125",
};

/**
 * Risk severity levels for analytics
 */
export const ANALYTICS_RISK_LEVELS = {
  OVERSHOOT: 'Overshoot',
  LIGHT: 'Light',
  SEVERE: 'Severe',
  MODERATE: 'Moderate',
  LIGHTNING: 'Lightning',
};

/**
 * Default values for the analytics view
 */
export const ANALYTICS_DEFAULT_VALUES = {
  CQL_SEVERE: 'Light',
  CQL_FECHA: '2024-07-30 00:00:00',
  MAP_LEFT_LAYER: 'VALIDATION_PLAN_v2:EU_0125_historical_view',
  MAP_CROSSBORDER_LAYER: 'VALIDATION_PLAN_v2:CROSSBORDER',
  SELECTED_MODEL: 'VALIDATION_PLAN_v2:EU_0125_historical_view',
  SELECTED_RISK: 'Light',
  TAB_INDEX: 0,
};

/**
 * Gets the right layer based on left layer and severity for analytics
 * @param {string} leftLayer - The selected left map layer
 * @param {string} severity - The selected severity level
 * @returns {string} - The corresponding right layer
 */
export const getAnalyticsRightLayer = (leftLayer, severity) => {
  const layerMappings = {
    'VALIDATION_PLAN_v2:EU_025_historical_view': {
      [ANALYTICS_RISK_LEVELS.OVERSHOOT]: 'TARGETS_OT:ot_EU_025_view',
      [ANALYTICS_RISK_LEVELS.LIGHT]: 'TARGETS_RDT:rdt_EU_025_view',
      [ANALYTICS_RISK_LEVELS.SEVERE]: 'TARGETS_RDT:rdt_EU_025_view',
      [ANALYTICS_RISK_LEVELS.MODERATE]: 'TARGETS_RDT:rdt_EU_025_view',
      [ANALYTICS_RISK_LEVELS.LIGHTNING]: 'TARGETS_LIGHTNINGS:lightnings_EU_025_view',
    },
    'VALIDATION_PLAN_v2:EU_0125_historical_view': {
      [ANALYTICS_RISK_LEVELS.OVERSHOOT]: 'TARGETS_OT:ot_EU_0125_view',
      [ANALYTICS_RISK_LEVELS.LIGHT]: 'TARGETS_RDT:rdt_EU_0125_view',
      [ANALYTICS_RISK_LEVELS.SEVERE]: 'TARGETS_RDT:rdt_EU_0125_view',
      [ANALYTICS_RISK_LEVELS.MODERATE]: 'TARGETS_RDT:rdt_EU_0125_view',
      [ANALYTICS_RISK_LEVELS.LIGHTNING]: 'TARGETS_LIGHTNINGS:lightnings_EU_0125_view',
    },
  };

  return layerMappings[leftLayer]?.[severity] || leftLayer;
};

/**
 * Gets the pretty display name for an analytics model
 * @param {string} modelKey - The model key
 * @returns {string} - The display name
 */
export const getAnalyticsModelDisplayName = (modelKey) => {
  return ANALYTICS_MODEL_DISPLAY_NAMES[modelKey] || modelKey;
};

/**
 * Gets the metrics name for an analytics model
 * @param {string} modelKey - The model key
 * @returns {string} - The metrics name
 */
export const getAnalyticsMetricsName = (modelKey) => {
  return ANALYTICS_METRICS_NAMES[modelKey];
};

/**
 * Gets the crossborder metrics name for an analytics model
 * @param {string} modelKey - The model key
 * @returns {string} - The crossborder metrics name
 */
export const getAnalyticsCrossborderMetricsName = (modelKey) => {
  return ANALYTICS_CROSSBORDER_METRICS_NAMES[modelKey];
};
